import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, User, Bot, Send, Search, Sparkles, Filter, ChevronRight, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { chatApi } from '../lib/api';
import { format, formatDistanceToNow } from 'date-fns';
import PageLoadingState from '../components/PageLoadingState';

export default function AdminChat() {
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [replying, setReplying] = useState(false);
  const [search, setSearch] = useState('');
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [selectedSession?.messages]);

  const fetchSessions = useCallback(async () => {
    try {
      const { data } = await chatApi.getAllSessions();
      setSessions(data);
      setSelectedSession(prev => {
        if (!prev) return prev;
        const updated = data.find(s => s.id === prev.id);
        if (updated && JSON.stringify(updated.messages) !== JSON.stringify(prev.messages)) {
          return updated;
        }
        return prev;
      });
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
    const interval = setInterval(fetchSessions, 10000);
    return () => clearInterval(interval);
  }, [fetchSessions]);

  const handleReply = async (e) => {
    e.preventDefault();
    if (!message.trim() || !selectedSession) return;

    setReplying(true);
    try {
      const { data } = await chatApi.adminReply(selectedSession.id, { message: message.trim() });
      setSelectedSession(prev => ({
        ...prev,
        messages: [...prev.messages, data]
      }));
      setMessage('');
      fetchSessions();
    } catch (error) {
      console.error('Failed to send reply:', error);
    } finally {
      setReplying(false);
    }
  };

  const filteredSessions = sessions.filter(s => 
    s.user_name?.toLowerCase().includes(search.toLowerCase()) || 
    s.user_email?.toLowerCase().includes(search.toLowerCase()) ||
    s.id.includes(search)
  );

  const groupedSessions = filteredSessions.reduce((groups, session) => {
    const date = format(new Date(session.created_at), 'MMM dd, yyyy');
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(session);
    return groups;
  }, {});

  return (
    <div className="min-h-screen pt-32 px-4 md:px-8 lg:px-12 pb-20 bg-dot-pattern">
      <div className="max-w-7xl mx-auto h-[calc(100vh-250px)]">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="h-full flex flex-col"
        >
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="h-2 w-2 rounded-full bg-primary animate-pulse" />
                <span className="text-xs font-bold uppercase tracking-widest text-primary">Live Support</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight bg-gradient-to-r from-foreground to-foreground/60 bg-clip-text text-transparent">
                Conversations
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" className="rounded-xl border-white/10 glass-morphic">
                <Filter className="h-4 w-4 mr-2" />
                Filter
              </Button>
              <div className="text-sm font-medium px-4 py-2 rounded-xl bg-primary/10 text-primary border border-primary/20">
                {sessions.filter(s => s.status === 'open').length} Active Chats
              </div>
            </div>
          </div>

          <div className="flex-1 flex gap-6 overflow-hidden">
            {/* Sidebar */}
            <Card className="w-80 md:w-96 flex flex-col border-white/10 bg-card/40 backdrop-blur-xl rounded-3xl overflow-hidden border shadow-2xl">
              <CardHeader className="p-6 border-b border-white/5">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input 
                    placeholder="Search by name or email..." 
                    className="pl-11 h-12 rounded-2xl bg-secondary/30 border-none focus-visible:ring-1 focus-visible:ring-primary/30 transition-all" 
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </CardHeader>
              <ScrollArea className="flex-1">
                <div className="p-3 space-y-4">
                  <AnimatePresence mode="popLayout">
                    {loading && sessions.length === 0 ? (
                      <PageLoadingState title="Loading conversations" className="min-h-[28rem] pt-0" />
                    ) : filteredSessions.length === 0 ? (
                      <div className="text-center py-20 text-muted-foreground">
                        <MessageCircle className="h-12 w-12 mx-auto mb-4 opacity-10" />
                        <p className="text-sm font-medium">No conversations found</p>
                      </div>
                    ) : (
                      Object.entries(groupedSessions).map(([date, dateSessions]) => (
                        <div key={date} className="space-y-2">
                          <h4 className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground px-3 mb-1">
                            {date}
                          </h4>
                          {dateSessions.map((session) => (
                            <motion.button
                              layout
                              key={session.id}
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              onClick={() => setSelectedSession(session)}
                              className={`w-full text-left p-4 rounded-2xl transition-all relative group ${
                                selectedSession?.id === session.id 
                                ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20' 
                                : 'hover:bg-white/5 bg-transparent'
                              }`}
                            >
                              <div className="flex justify-between items-start mb-2">
                                <div className="flex items-center gap-3">
                                  <div className={`h-10 w-10 rounded-xl flex items-center justify-center font-bold text-sm ${
                                    selectedSession?.id === session.id ? 'bg-white/20' : 'bg-primary/10 text-primary'
                                  }`}>
                                    {session.user_name ? session.user_name[0].toUpperCase() : 'G'}
                                  </div>
                                  <div className="overflow-hidden">
                                    <p className="font-bold truncate text-[15px]">{session.user_name || 'Guest User'}</p>
                                    <div className="flex items-center gap-1.5 opacity-60">
                                      <Clock className="h-3 w-3" />
                                      <span className="text-[10px] font-bold uppercase tracking-tighter">
                                        {format(new Date(session.updated_at), 'h:mm a')}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <span className={`text-[10px] px-2 py-1 rounded-lg font-bold uppercase tracking-wider ${
                                  session.status === 'open' 
                                  ? (selectedSession?.id === session.id ? 'bg-white/20 text-white' : 'bg-orange-500/10 text-orange-500') 
                                  : (selectedSession?.id === session.id ? 'bg-white/20 text-white' : 'bg-green-500/10 text-green-500')
                                }`}>
                                  {session.status}
                                </span>
                              </div>
                              <p className={`text-xs truncate font-medium ${selectedSession?.id === session.id ? 'text-white/80' : 'text-muted-foreground'}`}>
                                {session.messages[session.messages.length - 1]?.message || 'No messages yet'}
                              </p>
                              {selectedSession?.id !== session.id && (
                                <div className="absolute right-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <ChevronRight className="h-4 w-4" />
                                </div>
                              )}
                            </motion.button>
                          ))}
                        </div>
                      ))
                    )}
                  </AnimatePresence>
                </div>
              </ScrollArea>
            </Card>

            {/* Chat Content */}
            <Card className="flex-1 flex flex-col border-white/10 bg-card/40 backdrop-blur-xl rounded-3xl overflow-hidden border shadow-2xl relative">
              {selectedSession ? (
                <>
                  <div className="p-6 border-b border-white/5 flex flex-row items-center justify-between bg-white/5">
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 rounded-2xl bg-primary flex items-center justify-center text-primary-foreground font-bold text-lg">
                        {selectedSession.user_name ? selectedSession.user_name[0].toUpperCase() : 'G'}
                      </div>
                      <div>
                        <CardTitle className="text-xl font-bold">{selectedSession.user_name || 'Guest User'}</CardTitle>
                        <p className="text-xs text-muted-foreground font-medium">{selectedSession.user_email || 'No email associated'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-[10px] font-bold px-3 py-1.5 rounded-xl bg-white/5 border border-white/10 text-muted-foreground uppercase tracking-widest">
                        ID: {selectedSession.id.slice(0, 8)}
                      </div>
                    </div>
                  </div>
                  
                  <CardContent className="flex-1 p-0 overflow-hidden relative">
                    <div className="absolute inset-0 bg-dot-pattern opacity-5" />
                    <ScrollArea className="h-full p-6 relative z-10">
                      <div className="space-y-6">
                        {selectedSession.messages.map((msg, i) => (
                          <motion.div 
                            key={i}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className={`flex ${msg.sender === 'user' ? 'justify-start' : 'justify-end'}`}
                          >
                            <div className={`flex gap-4 max-w-[80%] ${msg.sender === 'user' ? 'flex-row' : 'flex-row-reverse'}`}>
                              <div className={`h-10 w-10 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-sm border border-white/10 ${
                                msg.sender === 'user' ? 'bg-secondary text-primary' : msg.sender === 'ai' ? 'bg-indigo-500/20 text-indigo-500' : 'bg-primary text-white'
                              }`}>
                                {msg.sender === 'user' ? <User className="h-5 w-5" /> : msg.sender === 'ai' ? <Sparkles className="h-5 w-5" /> : <User className="h-5 w-5" />}
                              </div>
                              <div className={`flex flex-col ${msg.sender === 'user' ? 'items-start' : 'items-end'}`}>
                                <div className={`p-4 rounded-2xl shadow-sm text-sm leading-relaxed ${
                                  msg.sender === 'user' 
                                  ? 'bg-card border border-white/5 rounded-tl-none' 
                                  : msg.sender === 'ai'
                                  ? 'bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 rounded-tr-none'
                                  : 'bg-primary text-primary-foreground rounded-tr-none shadow-lg shadow-primary/20'
                                }`}>
                                  {msg.message}
                                </div>
                                <span className="text-[10px] font-bold uppercase tracking-tighter text-muted-foreground/60 mt-2 px-1">
                                  {msg.sender === 'ai' ? 'AI Assistant' : msg.sender === 'user' ? 'Customer' : 'You'} • {format(new Date(msg.timestamp), 'h:mm a')}
                                </span>
                              </div>
                            </div>
                          </motion.div>
                        ))}
                        <div ref={scrollRef} className="h-2" />
                      </div>
                    </ScrollArea>
                  </CardContent>

                  <div className="p-6 border-t border-white/5 bg-white/5 backdrop-blur-md">
                    <form onSubmit={handleReply} className="flex gap-3 relative">
                      <Input
                        placeholder="Write your response..."
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        className="flex-1 h-14 rounded-2xl px-6 bg-secondary/30 border-none focus-visible:ring-1 focus-visible:ring-primary/30 transition-all font-medium"
                        disabled={replying}
                      />
                      <Button 
                        type="submit" 
                        disabled={!message.trim() || replying}
                        className="h-14 px-8 rounded-2xl shadow-xl shadow-primary/20 bg-primary hover:bg-primary/90 font-bold transition-all flex items-center gap-2"
                      >
                        {replying ? (
                          <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                          <>
                            <Send className="h-5 w-5" />
                            <span>Send Reply</span>
                          </>
                        )}
                      </Button>
                    </form>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center text-center p-20">
                  <div className="w-24 h-24 rounded-full bg-primary/5 flex items-center justify-center mb-6 border border-primary/10">
                    <MessageCircle className="h-12 w-12 text-primary/20" />
                  </div>
                  <h3 className="text-2xl font-bold mb-2">Ready to assist?</h3>
                  <p className="text-muted-foreground max-w-xs mx-auto">Select a customer conversation from the list to start providing support.</p>
                </div>
              )}
            </Card>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
